import { LimitExceededError } from './error'

import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type { Operator, OperatorCreateParams, OperatorUpdateParams } from '~/entities/operator'
import type { SortOptions } from '~/entities/supabase'

const MAX_OPERATORS_COUNT = 300

export const getOperators = async (
  locationId: Operator['location_id'],
  sortOptions: SortOptions<Operator> = [
    { column: 'name', order: 'asc' },
    { column: 'id', order: 'asc' },
  ]
): Promise<Operator[]> => {
  const query = useSupabaseClient().from('operators').select('*').eq('location_id', locationId)

  for (const { column, order } of sortOptions) {
    query.order(column, { ascending: order === 'asc' })
  }

  const { data, error } = await query

  if (error) throw error
  return data
}

export const createOperator = async (params: OperatorCreateParams): Promise<Operator> => {
  const count = await getOperatorsCount(params.location_id)
  if (count >= MAX_OPERATORS_COUNT) throw new LimitExceededError()
  const { data, error } = await useSupabaseClient().from('operators').insert(params).single()
  if (error) throw error
  return data
}

export const updateOperator = async (id: Operator['id'], params: OperatorUpdateParams) => {
  const { data, error } = await useSupabaseClient()
    .from('operators')
    .update(params)
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return data
}

const getOperatorsCount = async (locationId: Operator['location_id']): Promise<number> => {
  const { count, error } = await useSupabaseClient()
    .from('operators')
    .select('id', { count: 'exact', head: true })
    .eq('location_id', locationId)
  if (error) throw error
  return count ?? 0
}
